// app/javascript/controllers/request_pricing_controller.js
import { Controller } from "@hotwired/stimulus";

// === Helpers (same pattern as demo form) ===
function camelizeActionForJs(action) {
  return (action || "")
    .toString()
    .replace(/\W/g, "_")
    .split(/\/|_/)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join("");
}

// Ask the recaptcha gem to re-execute the action and fill its hidden input.
// Requires the view to include:
//   <%= turbo_frame_tag "recaptcha_pricing" do %>
//     <%= recaptcha_v3 action: "submit_pricing_form", turbo: true %>
//   <% end %>
async function recaptchaGetFreshTokenFor(action, { timeoutMs = 8000 } = {}) {
  const fnName = `executeRecaptchaFor${camelizeActionForJs(action)}Async`;
  const fn = window[fnName];
  if (typeof fn !== "function") {
    throw new Error(
      `reCAPTCHA function ${fnName} not found. Make sure recaptcha_v3(action: "${action}", turbo: true) is present on this page (inside a <turbo-frame>).`
    );
  }

  const timer = new Promise((_, rej) =>
    setTimeout(() => rej(new Error("recaptcha execute timeout")), timeoutMs)
  );

  const token = await Promise.race([fn(), timer]).catch((e) => {
    console.warn("[reCAPTCHA] execute timeout/error:", e);
    return "";
  });

  // Ensure the gem’s hidden field actually got populated
  const sel = `input[name="g-recaptcha-response-data[${action}]"],textarea[name="g-recaptcha-response-data[${action}]"]`;
  const input = document.querySelector(sel);
  if (input?.value) return input.value;

  if (token) { if (input) input.value = token; return token; }
  return ""; // caller decides whether to block submit
}

export default class extends Controller {
  static targets = ["selectAllButton", "productCheckbox", "errorContainer", "submitButton"];

  connect() {
    this.captureUTMParamsOnce();
    this.applyStoredUTMParamsToForm();
    this.captureIPAddress();
    this.captureReferrer();
    this.bindSectionButtons();
  }

  // Keep a handle to THIS form element
  get form() { return this.element; }

  // ----- Referrer / IP / UTM -----
  captureReferrer() {
    const referrer = document.referrer;
    if (referrer && !localStorage.getItem("referrer")) {
      localStorage.setItem("referrer", referrer);
    }
    let referrerField = this.form.querySelector("input[name='referrer']");
    if (!referrerField) {
      referrerField = document.createElement("input");
      referrerField.type = "hidden";
      referrerField.name = "referrer";
      this.form.appendChild(referrerField);
    }
    referrerField.value = localStorage.getItem("referrer") || "";
  }

  captureIPAddress() {
    fetch("https://api.ipify.org?format=json")
      .then(r => r.json())
      .then(data => {
        let ipAddressField = this.form.querySelector("input[name='ipAddress']");
        if (!ipAddressField) {
          ipAddressField = document.createElement("input");
          ipAddressField.type = "hidden";
          ipAddressField.name = "ipAddress";
          this.form.appendChild(ipAddressField);
        }
        ipAddressField.value = data.ip;
      })
      .catch(err => console.error("Error fetching IP address:", err));
  }

  captureUTMParamsOnce() {
    const urlParams = new URLSearchParams(window.location.search);
    ["utm_campaign", "utm_medium", "utm_source"].forEach(param => {
      const value = urlParams.get(param);
      if (value) localStorage.setItem(param, value);
    });

    // HubSpot hutk (best-effort)
    let hutkMatch = document.cookie.match(/hubspotutk=([a-zA-Z0-9-_]+);?/);
    if (hutkMatch) {
      localStorage.setItem("hutk", hutkMatch[1]);
    } else {
      setTimeout(() => {
        hutkMatch = document.cookie.match(/hubspotutk=([a-zA-Z0-9-_]+);?/);
        if (hutkMatch) localStorage.setItem("hutk", hutkMatch[1]);
      }, 1000);
    }

    this.applyStoredUTMParamsToForm();
  }

  applyStoredUTMParamsToForm() {
    ["utm_campaign", "utm_medium", "utm_source"].forEach(param => {
      const value = localStorage.getItem(param) || "";
      let input = this.form.querySelector(`input[name='${param}__c']`);
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = `${param}__c`;
        this.form.appendChild(input);
      }
      input.value = value;
    });
  }

  // ----- UI helpers -----
  bindSectionButtons() {
    document.querySelectorAll('[data-action="click->request-modal#open"][data-section]').forEach(button => {
      button.addEventListener('click', () => {
        this.clearAllCheckboxes();
        const section = button.getAttribute('data-section');
        setTimeout(() => {
          if (section === "All") {
            this.checkAllCheckboxes();
          } else {
            const checkbox = this.form.querySelector(`input[name="product_interest[]"][value="${section}"]`);
            if (checkbox) {
              checkbox.checked = true;
              this.triggerChangeEvent(checkbox);
            }
          }
        }, 500);
      });
    });
  }

  triggerChangeEvent(el) {
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  clearAllCheckboxes() {
    this.form.querySelectorAll('input[name="product_interest[]"]').forEach(cb => {
      cb.checked = false;
      this.triggerChangeEvent(cb);
    });
  }

  checkAllCheckboxes() {
    this.form.querySelectorAll('input[name="product_interest[]"]').forEach(cb => {
      cb.checked = true;
      this.triggerChangeEvent(cb);
    });
  }

  isAtLeastOneChecked() {
    if (!this.hasProductCheckboxTarget) return true; // fail-open if targets not present
    return this.productCheckboxTargets.some(cb => cb.checked);
  }

  showErrorMessage() {
    if (this.hasErrorContainerTarget) this.errorContainerTarget.classList.remove("hidden");
  }
  hideErrorMessage() {
    if (this.hasErrorContainerTarget) this.errorContainerTarget.classList.add("hidden");
  }

  // ----- Submit -----
  async submitForm(event) {
    event.preventDefault();

    if (!this.isAtLeastOneChecked()) {
      this.showErrorMessage();
      return;
    }
    this.hideErrorMessage();

    const btn = this.hasSubmitButtonTarget ? this.submitButtonTarget : this.form.querySelector("[type='submit']");
    if (btn) btn.disabled = true;

    try {
      // Fresh v3 token for this action (let the gem fill its hidden input)
      await recaptchaGetFreshTokenFor("submit_pricing_form");

      const formData = new FormData(this.form);
      formData.append("pageUri", window.location.href);
      formData.append("pageName", document.title);

      const hutk = localStorage.getItem("hutk") || "";
      formData.append("hutk", hutk);

      const response = await fetch(this.form.action, {
        method: "POST",
        headers: { "X-CSRF-Token": formData.get("authenticity_token") },
        body: formData,
      });

      const ct = response.headers.get("content-type") || "";
      const result = ct.includes("application/json") ? await response.json() : null;

      if (!response.ok) throw new Error(result?.message || `HTTP ${response.status}`);

      if (result?.status === "ok") {
        this.logHubSpotPageView();
        this.loadRevenueHeroWidget?.(result.hero_data?.router_id, result.hero_data?.fields);
      } else {
        console.warn("[REQUEST PRICING] server message:", result?.message);
      }
    } catch (err) {
      console.error("[REQUEST PRICING] submitForm error:", err);
      alert("Something went wrong submitting the form. Please refresh the page and try again.");
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  logHubSpotPageView() {
    if (typeof window._hsq === "undefined") {
      setTimeout(() => this.logHubSpotPageView(), 500);
      return;
    }
    window._hsq.push(["trackPageView"]);
  }

  toggleSelectAll() {
    this.allSelected = !this.allSelected;
    if (this.hasProductCheckboxTarget) {
      this.productCheckboxTargets.forEach(cb => (cb.checked = this.allSelected));
    }
    if (this.hasSelectAllButtonTarget) {
      this.selectAllButtonTarget.textContent = this.allSelected ? "Deselect All" : "Select All";
    }
  }

  loadRevenueHeroWidget(routerId, fields) {
    try {
      const hero = new RevenueHero({ routerId, showLoader: true });
      hero.submit(fields).then(rhData => hero.dialog.open(rhData));
    } catch (e) {
      console.error("Error with RevenueHero:", e);
    }
  }
}